<script setup lang="ts">
import { SrcSet } from '~/composables/useSrcSet'

export type QuoteData = {
    description: string
    image?: Image
    name: string
    position: string
    link: CTA
}

export interface QuoteProps {
    data: QuoteData
    descriptionClasses?: string
}

const props = defineProps<QuoteProps>()
const imageSrc = computed((): SrcSet | undefined => {
    if (!props.data.image) return
    return useSrcSet(props.data.image.path)
})
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <figure
    class="rounded quote-wrapper p-md-5 p-2"
    data-cy="quote_person"
  >
    <Tracker :data="{ title: props.data.name }">
      <blockquote>
        <Typo class="px-md-5 px-3 pt-md-5 pt-3">
          <div
            class="mb-5 fw-normal"
            :class="props.descriptionClasses"
            v-html="props.data.description"
          />
        </Typo>
      </blockquote>
      <figcaption>
        <Box
          decor-position="top-left"
          :padding="''"
          size="small"
          color="gray-1"
          class="rounded px-3 px-md-5 py-2 mt-10"
        >
          <div class="d-flex align-items-center">
            <img
              v-if="props.data.image"
              class="rounded-circle quote-img d-block flex-shrink-0"
              loading="lazy"
              decoding="async"
              :srcset="imageSrc?.srcSet"
              sizes="60px"
              height="60"
              width="60"
              :alt="`${props.data.name} - ${props.data.position}`"
            >
            <div
              :class="{ 'ps-md-5 ps-3': props.data.image?.path }"
            >
              <strong class="d-block mb-0 fs-smaller">
                {{ props.data.name }}
              </strong>
              <VNuxtLink
                v-if="props.data.link"
                :to="
                  props.data.link.isExternal
                    ? props.data.link.link
                    : useNamedRoutes({
                      name: 'assistance-services-detail',
                      params: {
                        id: props.data.link.link,
                      },
                    })
                "
                class="fw-bold fs-xsmall"
                :target="
                  props.data.link.isExternal
                    ? '_blank'
                    : '_self'
                "
                :label="props.data.link.text"
              >
                {{ props.data.link.text }}
              </VNuxtLink>
              <cite class="fst-normal fs-small">
                {{ props.data.position }}
              </cite>
            </div>
          </div>
        </Box>
      </figcaption>
    </Tracker>
  </figure>
</template>

<style lang="scss" scoped>
@import './Quote.scss';
</style>
